import { createTheme, type CSSVariablesResolver, type MantineColorsTuple } from '@mantine/core';

const ink: MantineColorsTuple = [
  '#f7f5f3',
  '#e9e8e8',
  '#d3cfca',
  '#bdb4aa',
  '#aa9d8e',
  '#9f8f7c',
  '#9a8772',
  '#867560',
  '#786754',
  '#1f1a14',
];

/**
 * Theme-level WCAG 2.2 AA contrast resolver (idea
 * `2026-06-20-wandero-admin-theme-aa-contrast`). Two Mantine defaults fail AA on
 * this surface's warm cream backgrounds; both are fixed once here so every
 * `c="dimmed"` and every `<Badge variant="light" color="green">` clears 4.5:1
 * without per-page overrides (which would re-introduce the inconsistency).
 *
 * Backgrounds that text actually sits on (worst-case checked against all):
 *   white #ffffff (cards/tables) · page #fbf7f2 (AppShell.Main) ·
 *   ink.0 #f7f5f3 (active/hover) · striped row gray.0 #f8f9fa.
 *
 * (1) --mantine-color-dimmed: Mantine default gray.6 #868e96 = 3.05–3.32:1.
 *     Replaced with warm taupe #766c5e (hsl 35°, aligned with the cream+ink
 *     --w-muted token rather than Mantine's cold blue-gray) → worst 4.74:1.
 *     Light mode only; dark mode keeps Mantine's default.
 *
 * (2) --mantine-color-green-light-color: Mantine's light-variant green text is
 *     green.9 #2b8a3e, which on the 10%-alpha green tint = 3.79–4.03:1
 *     (autoContrast does not lift light-variant text — it only re-derives text
 *     for solid-fill variants). Darkened to #1f6b2e (still clearly green,
 *     hue 132°) → worst 5.70:1 over the tint on white / page / striped.
 *
 * (3) --mantine-color-yellow-light-color: Mantine's light-variant yellow text is
 *     yellow.9 #e67700, which on the 10%-alpha yellow tint = only 2.65–2.81:1
 *     (white/page) — a fail the green/dimmed fixes (2026-06-20) missed because no
 *     surface leaned on yellow light badges until the home dashboard (Categories
 *     "N hidden", Wallpapers "N draft", Needs-attention count) and the shared
 *     WpStatusBadge `review` pill. Darkened to #7a5800 (still clearly amber-gold,
 *     hue 43°) → worst 5.76:1 over the tint on page, 6.11:1 on white. Repairs
 *     both surfaces at once; no per-badge override.
 *
 * Filled-variant text and the badge fill tint are untouched, so the change is
 * surgical: it only darkens the failing light-variant text tokens.
 */
const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    // Light-variant green badge text — color-scheme independent (the light tint
    // is the same warm-on-light surface in this single-scheme admin).
    '--mantine-color-green-light-color': '#1f6b2e',
    // Light-variant yellow badge text — same single-scheme rationale (see note 3).
    '--mantine-color-yellow-light-color': '#7a5800',
  },
  light: {
    '--mantine-color-dimmed': '#766c5e',
  },
  dark: {},
});

export const theme = createTheme({
  primaryColor: 'ink',
  primaryShade: 9,
  colors: { ink },
  headings: { fontFamily: 'Fraunces, Georgia, serif' },
  fontFamilyMonospace: 'JetBrains Mono, ui-monospace, SFMono-Regular, Menlo, monospace',
});

export { cssVariablesResolver };
